import { useRef, useState } from 'react';
import { Box, Typography, Button, IconButton, Alert } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Outlet, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CustomCellRendererProps } from 'ag-grid-react';
import { ColDef, GridApi, SelectionChangedEvent, IRowNode } from 'ag-grid-community';
import { useConfirm } from '../contexts/confirm.context';
import {
  ArcgisCivil3dSettingConfigurationApiModel,
  ArcgisCivil3dSettingConfigurationRelationship,
  WithTemplate,
} from '../../../types/models/arcgis-civil3d-setting-configuration.model';
import { InfniteScrollingGrid } from '../components/AgGrid/InfiniteScrollingGrid';
import { InfiniteScrollingGridSelectAllHeader } from '../components/AgGrid/InfiniteScrollingGridSelectAllHeader';
import { searchSettingConfigurations } from '../api/arcgis-civil3d-setting-configuration.api';
import {
  useDeleteSettingConfiguration,
  useDuplicateSettingConfigurations,
} from '../hooks/api/use-arcgis-civil3d-setting-configurations.hook';
import { CIVIL_3D_SETTING_CONFIGURATION_DUPLICATE_COUNT_LIMIT } from '../../../types/constants/arcgis-civild3d-setting';

export const SettingConfigurations = () => {
  const gridApi = useRef<GridApi>();
  const navigate = useNavigate();
  const [showConfirm] = useConfirm();
  const [selectedRows, setSelectedRows] = useState<IRowNode<ArcgisCivil3dSettingConfigurationApiModel>[]>([]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [deleteSettingConfiguration, { loading: deletingSettingConfiguration, error: deleteError }] =
    useDeleteSettingConfiguration();
  const [duplicateSettingConfigurations, { loading: duplicatingSettingConfigurations, error: duplicateError }] =
    useDuplicateSettingConfigurations();

  const columns: ColDef<ArcgisCivil3dSettingConfigurationApiModel & WithTemplate>[] = [
    {
      field: 'name',
      valueGetter: ({ data }) => data?.name,
      sortable: true,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'template',
      colId: 'template.name',
      valueGetter: ({ data }) => data?.template?.name,
      sortable: true,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      headerName: 'Actions',
      minWidth: 200,
      maxWidth: 200,
      cellRenderer: ({ api, node: row }: CustomCellRendererProps<ArcgisCivil3dSettingConfigurationApiModel>) => (
        <>
          <IconButton onClick={() => navigate(`edit/${row.id}/cogo-points`)}>
            <EditIcon />
          </IconButton>
          <IconButton
            onClick={() => {
              showConfirm({
                onConfirm: async () => {
                  if (row.id) {
                    await deleteSettingConfiguration({ settingConfigurationId: +row.id });
                    gridApi.current?.setNodesSelected({
                      nodes: [row],
                      newValue: false,
                    });

                    api.refreshInfiniteCache();
                  }
                },
                title: 'Delete Setting Configuration',
                message: `Are you sure you want to delete setting configuration "${row.data?.name}"?`,
                error: deleteError?.response?.data?.message,
              });
            }}
          >
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  const getSettingConfigurations: Parameters<
    typeof InfniteScrollingGrid<ArcgisCivil3dSettingConfigurationApiModel>
  >[0]['onGetRows'] = async ({ cursor, limit, filterQuery, sortQuery }) => {
    const { data } = await searchSettingConfigurations({
      name: filterQuery?.name,
      templateName: filterQuery?.template,
      include: [ArcgisCivil3dSettingConfigurationRelationship.TEMPLATE],
      cursor,
      limit,
      sort: sortQuery,
    });

    return data;
  };

  const handleSelectionChange = ({ api }: Pick<SelectionChangedEvent, 'api'>) => {
    setSelectedRows(api.getSelectedRows());
  };

  const handleBulkDuplicate = async () => {
    const idsToDuplicate = selectedRows.map(({ id }) => Number(id));

    if (idsToDuplicate.length === 0) return;

    showConfirm({
      onConfirm: async () => {
        await duplicateSettingConfigurations({ settingConfigurationIds: idsToDuplicate });

        const nodes: IRowNode[] = [];

        gridApi.current?.forEachNode((row) => nodes.push(row));
        gridApi.current?.setNodesSelected({ nodes, newValue: false });

        setSelectedRows([]);

        gridApi.current?.refreshInfiniteCache();
      },

      title: `Duplicate ArcGIS Civil 3D Setting Configurations`,
      message: `Are you sure you want to duplicate ${selectedRows.length} setting configuration(s) (Limit ${CIVIL_3D_SETTING_CONFIGURATION_DUPLICATE_COUNT_LIMIT})?`,
    });
  };

  return (
    <Box sx={{ height: '100%' }}>
      <Outlet />
      <Helmet title="ArcGIS - Civil 3D Data Editor" />
      <Box display="flex" flexDirection="column" sx={{ height: '100%', p: '0 12px' }}>
        <Box display="flex" sx={{ m: '10px 0' }} justifyContent="space-between">
          <Typography variant="h5">ArcGIS - Civil 3D Data Editor</Typography>

          <Box display="flex" gap={1.5}>
            <Button color="primary" variant="contained" startIcon={<AddIcon />} onClick={handleBulkDuplicate}>
              Duplicate Configurations
            </Button>
            <Button color="primary" variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`add`)}>
              Add Configuration
            </Button>
          </Box>
        </Box>
        {duplicateError ? (
          <Alert sx={{ my: '1rem' }} severity="error">
            {duplicateError?.response?.data?.message}
          </Alert>
        ) : null}

        <Box alignSelf={'stretch'} flexGrow={1} sx={{ pb: '12px' }}>
          <InfniteScrollingGrid
            loading={deletingSettingConfiguration || duplicatingSettingConfigurations}
            onGridReady={({ api }) => {
              gridApi.current = api;
            }}
            selectionColumnDef={{
              pinned: 'left',
              headerComponent: InfiniteScrollingGridSelectAllHeader,
              headerComponentParams: { onSelectAll: (value: boolean) => setIsSelectAll(value), isSelectAll },
            }}
            rowSelection={{
              mode: 'multiRow',
              selectAll: 'filtered',
            }}
            columnDefs={columns}
            onGetRows={getSettingConfigurations}
            onSelectionChanged={handleSelectionChange}
          />
        </Box>
      </Box>
    </Box>
  );
};
